import config from '../../../Config'
import { useContext, useState, useEffect, useRef } from 'react';
import utils from '../../../utils';
import { Modal, Button, Select } from 'antd';
import AMapLoader from '@amap/amap-jsapi-loader';
import axios from "axios";
import { useTranslation } from 'react-i18next';

function MapLocationGetter({show, onCancel, onOK, centerPoint}) {
    const { t, i18n } = useTranslation();
    const setTip = useContext(utils.tipContext);

    const [location, setLocation] = useState({});
    const [pushPins, setPushPins] = useState([]);
    const [addressSelectOptions, setAddressSelectOptions] = useState([])
    const [center, setCenter] = useState([])
    const [searchValue, setSearchValue] = useState('');
    const mapDivRef = useRef(null);
    const mapRef = useRef(null);
    const markersRef = useRef([]);
    const aMapRef = useRef(null);
    const [langKey, setLangKey] = useState(0);

    useEffect(() => {
        const handleLangChange = () => {
            if (mapRef.current) {
                mapRef.current.destroy();
                mapRef.current = null;
            }
            aMapRef.current = null;
            markersRef.current = [];
            setTimeout(() => {
                AMapLoader.reset();
                const scripts = document.querySelectorAll('script[src*="webapi.amap.com"]');
                scripts.forEach(s => s.parentNode && s.parentNode.removeChild(s));
                const links = document.querySelectorAll('link[href*="webapi.amap.com"]');
                links.forEach(l => l.parentNode && l.parentNode.removeChild(l));
                setLangKey(prev => prev + 1);
            }, 500);
        };
        i18n.on('languageChanged', handleLangChange);
        return () => {
            i18n.off('languageChanged', handleLangChange);
        };
    }, [i18n]);

    useEffect(() => {
        if (!show || !mapDivRef.current) return;
        let isMounted = true;
        const timer = setTimeout(() => {
            AMapLoader.load({
                key: config.gaodeMapKey,
                version: '1.4.15',
            }).then((AMap) => {
                if (!isMounted || !mapDivRef.current) return;
                aMapRef.current = AMap;
                const map = new AMap.Map(mapDivRef.current, {
                    zoom: 5,
                    center: center.length ? [center[1], center[0]] : [0, 0],
                    lang: i18n.language === 'zh' ? 'zh_cn' : 'en',
                });
                mapRef.current = map;

                map.on('click', (e) => {
                    handleMapClick({ longitude: e.lnglat.getLng(), latitude: e.lnglat.getLat() });
                });
            });
        }, 300);

        return () => {
            isMounted = false;
            clearTimeout(timer);
            if (mapRef.current) {
                try {
                    mapRef.current.destroy();
                } catch (e) {
                    console.warn('Map destroy error:', e);
                }
                mapRef.current = null;
            }
            aMapRef.current = null;
        };
    }, [show, langKey]);

    useEffect(() => {
        const map = mapRef.current;
        if (!map) return;

        markersRef.current.forEach(m => map.remove(m));
        markersRef.current = [];

        pushPins.forEach(pin => {
            const marker = new window.AMap.Marker({
                position: [pin.location[1], pin.location[0]],
                content: pin.option?.text
                    ? `<div style="background:red;color:white;border-radius:50%;width:24px;height:24px;display:flex;align-items:center;justify-content:center;font-size:12px;">${pin.option.text}</div>`
                    : undefined,
                offset: new window.AMap.Pixel(-12, -12),
            });
            map.add(marker);
            markersRef.current.push(marker);
        });
    }, [pushPins]);

    useEffect(() => {
        const map = mapRef.current;
        if (!map || !center.length) return;
        map.setCenter([center[1], center[0]]);
    }, [center]);

    useEffect(() =>{
        const lng = parseFloat(centerPoint.Longitude);
        const lat = parseFloat(centerPoint.Latitude);
        if (isNaN(lng) || isNaN(lat)) {
            setPushPins([]);
            
        } else {
            const lt =  [lat, lng]
            setPushPins([{
                location: lt
            }])
            setCenter(lt)
        }
        setLocation({Longitude: isNaN(lng) ? undefined : lng, Latitude: isNaN(lat) ? undefined : lat})
    }, [centerPoint])

    useEffect(() => {
        let isMounted = true;
        const timer = setTimeout(() => {
            if (searchValue === '') {
                return;
            }
            if (!aMapRef.current) return;
            const AMap = aMapRef.current;
            AMap.plugin(['AMap.PlaceSearch'], () => {
                const placeSearch = new AMap.PlaceSearch({ pageSize: 10 });
                placeSearch.search(searchValue, (status, result) => {
                    if (!isMounted) return;
                    if (status === 'complete' && result.info === 'OK') {
                        const pois = result.poiList.pois;
                        const pins = [];
                        const addresses = [];
                        for (let i = 0; i < pois.length; i++) {
                            const location = [pois[i].location.lat, pois[i].location.lng];
                            const name = (i + 1) + ': ' + (pois[i].name || '');
                            pins.push({
                                location: location,
                                name: name,
                                option: {
                                    text: i + 1 + ''
                                }
                            });
                            addresses.push({
                                name: name,
                                location: location,
                            })
                        }
                        if (pins.length > 0) {
                            setCenter(pins[0].location)
                        }
                        setPushPins(pins);
                        const ls = addresses.map((item, index) => {
                            return (
                                <Select.Option key={index} value={item.name}>{item.name}</Select.Option>
                            );
                        });
                        setAddressSelectOptions(ls);
                    }
                });
            });
        }, 300);

        return () => {
            isMounted = false;
            clearTimeout(timer);
        };
    }, [searchValue, setTip, t]);

    const handleCancle = () => {
        onCancel();
    }

    const handleOK = () => {
        if (location.Latitude !== undefined && location.Longitude !== undefined) {
            onOK(location)
            return;
        }

        setTip({ open: true, severity: "warning", msg: t("No latitude and longitude were selected") });
    }

    const handleMapClick = (e) => {
        setPushPins([{
            location: [e.latitude, e.longitude],
        }]);

        setLocation({
            Longitude: e.longitude,
            Latitude: e.latitude,
        });
    }

    const handleSearchAddress = (value) => {
        setSearchValue(value);
    }

    const handleSelectAddress = (value) => {
        const index = pushPins.findIndex(item => item.name === value);
        const pin = pushPins[index]
        setPushPins([{
            location: pin.location
        }])
        setCenter(pin.location)
        setLocation({
            Longitude: pin.location[1],
            Latitude: pin.location[0],
        });
    }

    return (<Modal
        title={t("GPS Location")}
        visible={show}
        cancelText={t("Cancel")}
        okText={t("OK")}
        mask={true}
        maskClosable={false}
        width={600}
        forceRender
        onCancel={handleCancle}
        onOk={handleOK}
        footer={[
            <Button key="Cancel" onClick={handleCancle}>{t("Cancel")}</Button>,
            <Button key="OK" type="primary" onClick={handleOK}>{t("OK")}</Button>
        ]}
    >
        <div style={{
            display: "flex",
            flexDirection: "column"
        }}>
            <div style={{
                padding: "5px",
                display: "none",
            }}>
                <Select
                    showSearch
                    placeholder={t("Enter Address")}
                    defaultActiveFirstOption={false}
                    showArrow={false}
                    filterOption={false}
                    onSearch={handleSearchAddress}
                    onSelect={handleSelectAddress}
                    style={{width: '100%'}}
                >{ pushPins.map((item, index) => {
                    return (
                        <Select.Option key={index} value={item.name}>{item.name}</Select.Option>
                    );
                })}</Select>
            </div>
            <div style={{
                padding: "5px",
                display: "none",
            }}>
                {t("Location")}:({location.Longitude ? location.Longitude.toFixed(6) : "-"}, {location.Latitude ? location.Latitude.toFixed(6) : "-"})
            </div>
            <div ref={mapDivRef} style={{
                width: "100%",
                height: "400px",
            }}></div>
        </div>
    </Modal>);
}

export default MapLocationGetter;